import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { User, UserRole, signIn, signOut } from './supabase';

interface AuthState {
    user: User | null;
    isLoading: boolean;
    isAuthenticated: boolean;

    // Actions
    login: (email: string, password: string) => Promise<{ success: boolean; error?: string }>;
    logout: () => Promise<void>;
    checkSession: () => void;
    updateProfile: (data: Partial<User>) => void;
}

export const useAuthStore = create<AuthState>()(
    persist(
        (set, get) => ({
            user: null,
            isLoading: false,
            isAuthenticated: false,

            updateProfile: (data: Partial<User>) => {
                const currentUser = get().user;
                if (currentUser) {
                    set({
                        user: { ...currentUser, ...data }
                    });
                }
            },

            login: async (email: string, password: string) => {
                set({ isLoading: true });

                const { user, error } = await signIn(email, password);

                if (error || !user) {
                    set({ isLoading: false });
                    return { success: false, error: error || 'Giriş başarısız' };
                }

                set({
                    user,
                    isAuthenticated: true,
                    isLoading: false,
                });

                return { success: true };
            },

            logout: async () => {
                set({ isLoading: true });
                await signOut();
                set({
                    user: null,
                    isAuthenticated: false,
                    isLoading: false,
                });
            },

            // Sadece localStorage'dan oku, Supabase'e istek atma
            checkSession: () => {
                const state = get();
                // Persist middleware zaten user'ı localStorage'dan yüklüyor
                // Sadece isLoading'i false yap
                if (state.user && state.isAuthenticated) {
                    set({ isLoading: false });
                } else {
                    set({
                        user: null,
                        isAuthenticated: false,
                        isLoading: false,
                    });
                }
            },
        }),
        {
            name: 'saletone-auth',
            partialize: (state) => ({ user: state.user, isAuthenticated: state.isAuthenticated }),
        }
    )
);

// Helper hooks
export const useUser = () => useAuthStore((state) => state.user);
export const useIsAuthenticated = () => useAuthStore((state) => state.isAuthenticated);
export const useUserRole = (): UserRole | null => useAuthStore((state) => state.user?.role ?? null);

